import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useMenu } from "@/lib/queries";
import { money } from "@/lib/api";
import { SignatureDrinksSkeleton } from "@/components/skeletons";

function SignatureDrinks() {
  const { data, isLoading } = useMenu();

  const items = useMemo(() => {
    const raw = (data?.items || []).filter(i => i.image_url);
    const score = (i) => {
      const tags = i.tags || [];
      return (
        (tags.includes("popular") ? 3 : 0) +
        (tags.includes("new") ? 2 : 0) +
        (tags.includes("limited") ? 1 : 0)
      );
    };
    return [...raw].sort((a, b) => score(b) - score(a)).slice(0, 6);
  }, [data]);

  if (!isLoading && !items.length) return null;

  return (
    <section
      data-testid="home-signature-drinks"
      className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-primary/80">Crowd favorites</p>
          <h2 className="font-brand font-semibold text-[1.9rem] sm:text-3xl lg:text-4xl tracking-tight mt-2">
            Drinks worth the drive.
          </h2>
          <p className="mt-2 text-muted-foreground max-w-xl text-[15px] sm:text-base">
            Hand-cooked pearls, small-batch syrups, and that ridiculously fat straw.
          </p>
        </div>
        <Link
          to="/menu"
          data-testid="home-signature-view-menu-button"
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80"
        >
          See full menu <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {isLoading ? (
        <SignatureDrinksSkeleton />
      ) : (
        <div className="mt-7 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {items.map((it, idx) => (
            <DrinkTile key={it.id} item={it} featured={idx === 0} />
          ))}
        </div>
      )}
    </section>
  );
}

const DrinkTile = memo(function DrinkTile({ item, featured }) {
  return (
    <Link
      to="/menu"
      data-testid="home-signature-drink-tile"
      className={`bp-tile group relative overflow-hidden ${featured ? "md:col-span-2 md:row-span-2" : ""}`}
    >
      <div
        className={`relative w-full overflow-hidden rounded-xl bg-muted ${
          featured ? "aspect-[4/3] md:aspect-auto md:h-full md:min-h-[320px]" : "aspect-[4/5]"
        }`}
      >
        <img
          src={item.image_url}
          alt={`${item.name} — bubble tea at The Boba Place`}
          loading="lazy"
          width="640"
          height="800"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)" }}
        />
        <div className="absolute left-2.5 top-2.5 flex gap-1.5 flex-wrap">
          {(item.tags || []).slice(0, 2).map((t) => (
            <span
              key={t}
              className={`bp-chip backdrop-blur ${
                t === "popular" ? "bp-chip-popular" :
                t === "new" ? "bp-chip-new" :
                t === "seasonal" ? "bp-chip-seasonal" :
                t === "limited" ? "bp-chip-limited" : "bg-white/80"
              }`}
            >
              {t[0].toUpperCase() + t.slice(1)}
            </span>
          ))}
        </div>
        <div className="absolute inset-x-3 bottom-3 text-white">
          <div className="flex items-end justify-between gap-2">
            <h3 className="font-brand font-semibold text-base sm:text-lg leading-tight drop-shadow">
              {item.name}
            </h3>
            <span className="text-sm font-semibold tabular-nums bg-white/15 backdrop-blur rounded-full px-2 py-0.5 border border-white/25">
              {money(item.base_price_cents)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
});

export default memo(SignatureDrinks);
